import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Scale, PlusCircle, Pencil, Trash2 } from 'lucide-react';

const ASSET_TYPE_COLORS = { 'Trust': 'bg-indigo-500', 'Foundation': 'bg-pink-500' };

const AssetCardGrid = ({ relatedAssets, isReadOnly, handleReadOnlyAction, onEdit, onDelete, onAdd }) => {
  if (relatedAssets.length === 0) {
    return (
      <Card className="border-slate-200" data-testid="related-asset-empty-state">
        <CardContent className="py-8 px-4 sm:px-8">
          <div className="flex flex-col items-center text-center max-w-2xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 w-full">
              <div className="text-left bg-slate-50 rounded-lg p-3 border border-slate-200">
                <div className="text-xs font-semibold text-slate-700 mb-1">Examples</div>
                <div className="text-xs text-slate-600">A trust you set up for your children · A family foundation · An education trust for grandchildren</div>
              </div>
              <div className="text-left bg-slate-50 rounded-lg p-3 border border-slate-200">
                <div className="text-xs font-semibold text-slate-700 mb-1">Why track them?</div>
                <div className="text-xs text-slate-600">For estate planning, succession reporting & a full picture of the wealth under your control — without inflating your personal net worth.</div>
              </div>
            </div>
            <Button
              onClick={() => isReadOnly ? handleReadOnlyAction() : onAdd()}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="related-asset-empty-add-btn"
            >
              <PlusCircle className="w-4 h-4 mr-2" /> Add Your First Related Asset
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {relatedAssets.map((asset) => (
        <Card key={asset.asset_id} className="hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="text-lg font-semibold text-slate-900 mb-1">{asset.name}</CardTitle>
                <Badge className={`${ASSET_TYPE_COLORS[asset.type] || 'bg-slate-500'} text-white text-xs`}>{asset.type}</Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="text-sm text-slate-600"><span className="font-medium">Jurisdiction:</span> {asset.country}</div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t">
              <Button size="sm" variant="ghost" onClick={() => isReadOnly ? handleReadOnlyAction() : onEdit(asset)}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onDelete(asset.asset_id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            {asset.notes && <div className="text-xs text-slate-500 italic pt-2 border-t">{asset.notes}</div>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default AssetCardGrid;
